import { LinerWebhookMockPage } from "@/components/LinerWebhookMockPage";
import { SimulationUI } from "@/components/SimulationUI";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTheme } from "@/contexts/ThemeContext";
import { LogOut, Monitor, Moon, Ship, Sun } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { LoginPage } from "@/components/LoginPage";
import "./index.css";

const themeLabels: Record<"light" | "dark" | "system", string> = {
  light: "Light",
  dark: "Dark",
  system: "System",
};

function normalizeHashRoute(hash: string): "main" | "liner-mock" {
  const h = hash.replace(/^#/, "").replace(/^\/+/, "");
  if (h === "liner-webhook-mock" || h === "liner/mock") return "liner-mock";
  return "main";
}

export function App() {
  const { theme, setTheme } = useTheme();
  const mainRef = useRef<HTMLElement>(null);
  const [route, setRoute] = useState<"main" | "liner-mock">(() =>
    normalizeHashRoute(typeof window !== "undefined" ? window.location.hash : "")
  );
  const { isAuthenticated, logout } = useAuth();

  useEffect(() => {
    const onHash = () => setRoute(normalizeHashRoute(window.location.hash));
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return (
    <div className="relative z-10 flex min-h-0 w-full flex-1 flex-col overflow-hidden bg-transparent">
      <a
        href="#main-content"
        className="skip-link"
        onClick={(e) => {
          e.preventDefault();
          mainRef.current?.focus();
        }}
      >
        Skip to main content
      </a>
      <header className="sticky top-0 z-50 w-full shrink-0 border-b border-white/10 dark:border-white/5 bg-background/80 backdrop-blur-xl transition-all">
        <div className="container mx-auto flex h-16 max-w-[2200px] items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground text-sm font-bold shadow-lg shadow-primary/25 ring-1 ring-primary/20">
              S
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2 gap-y-1">
                <h1 className="font-heading text-lg sm:text-xl font-bold tracking-tight text-foreground">
                  Simulation UI
                </h1>
                <span className="inline-flex items-center rounded-md border border-border/80 bg-muted/50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Mock API
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5 max-w-md">
                Workspace for Customs and Liner mock endpoints &amp; webhooks
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-4">
            <nav className="flex flex-wrap items-center gap-1 text-sm" aria-label="Primary">
              <a
                href="#/"
                className={`rounded-md px-2.5 py-1.5 transition-colors ${
                  route === "main"
                    ? "bg-primary/15 font-medium text-foreground"
                    : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                }`}
              >
                Simulation
              </a>
              <a
                href="#/liner-webhook-mock"
                className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 transition-colors ${
                  route === "liner-mock"
                    ? "bg-primary/15 font-medium text-foreground"
                    : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                }`}
              >
                <Ship className="size-3.5 opacity-70" aria-hidden />
                Liner webhook mock
              </a>
            </nav>
            <Select value={theme} onValueChange={(v) => setTheme(v as "light" | "dark" | "system")}>
              <SelectTrigger className="w-[130px] h-9 bg-background/50 border-white/20 hover:border-primary/50 transition-colors">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="light" className="cursor-pointer">
                  <div className="flex items-center">
                    <Sun className="mr-2 size-4 text-orange-500" />
                    {themeLabels.light}
                  </div>
                </SelectItem>
                <SelectItem value="dark" className="cursor-pointer">
                  <div className="flex items-center">
                    <Moon className="mr-2 size-4 text-indigo-400" />
                    {themeLabels.dark}
                  </div>
                </SelectItem>
                <SelectItem value="system" className="cursor-pointer">
                  <div className="flex items-center">
                    <Monitor className="mr-2 size-4 text-muted-foreground" />
                    {themeLabels.system}
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            <button
              onClick={() => logout()}
              className="inline-flex h-9 items-center justify-center rounded-md border border-white/20 bg-background/50 px-3 text-sm font-medium hover:bg-muted/50 hover:text-foreground transition-colors"
              title="Sign out"
            >
              <LogOut className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>
      <main
        id="main-content"
        ref={mainRef}
        tabIndex={-1}
        className="mx-auto flex min-h-0 w-full max-w-[2200px] min-w-0 flex-1 flex-col px-4 py-6 sm:px-6"
      >
        {route === "liner-mock" ? <LinerWebhookMockPage /> : <SimulationUI />}
      </main>
    </div>
  );
}

export default App;
